import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  HelpCircle, 
  ChevronDown,
  ChevronUp,
  Search,
  MessageCircle,
  Mail,
  Phone,
  Home,
  CreditCard,
  Settings as SettingsIcon,
  Loader,
  AlertCircle
} from 'lucide-react';
import contentService from '../services/contentService';
import PermissionGate from '../components/PermissionGate';
import './FAQPage.css';

const FAQPage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // API state
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    { id: 'all', name: 'الكل', icon: <HelpCircle size={18} /> },
    { id: 'general', name: 'عام', icon: <Home size={18} /> },
    { id: 'account', name: 'الحساب', icon: <SettingsIcon size={18} /> },
    { id: 'offers', name: 'العروض', icon: <Home size={18} /> },
    { id: 'requests', name: 'الطلبات', icon: <Home size={18} /> },
    { id: 'payments', name: 'المدفوعات', icon: <CreditCard size={18} /> },
    { id: 'subscription', name: 'الاشتراك', icon: <CreditCard size={18} /> },
    { id: 'technical', name: 'تقني', icon: <SettingsIcon size={18} /> },
    { id: 'other', name: 'أخرى', icon: <HelpCircle size={18} /> }
  ];

  // Fetch FAQs from API
  const fetchFAQs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      const response = await contentService.getFAQs(params);
      if (response.success) {
        setFaqs(response.data.faqs || response.data || []);
      } else {
        setError(response.message || 'Failed to fetch FAQs');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchFAQs();
  }, [fetchFAQs]);

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.includes(searchQuery) || faq.answer.includes(searchQuery);
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <PermissionGate feature="PAGE_FAQ" type="page">
    <div className="faq-page">
      <div className="page-header">
        <div className="header-title">
          <HelpCircle size={28} />
          <h1>{t('faq.title')}</h1>
        </div>
      </div>

      <div className="faq-intro">
        <h2>كيف يمكننا مساعدتك؟</h2>
        <p>ابحث في الأسئلة الشائعة أو تواصل معنا مباشرة</p>
        <div className="search-box large">
          <Search size={22} />
          <input 
            type="text"
            placeholder="ابحث عن سؤالك..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="categories-tabs">
        {categories.map(category => (
          <button 
            key={category.id}
            className={`category-tab ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category.id)}
          >
            {category.icon}
            {category.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-state">
          <Loader className="spinner" size={40} />
          <p>جاري تحميل الأسئلة الشائعة...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <AlertCircle size={64} />
          <h3>حدث خطأ</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchFAQs}>
            إعادة المحاولة
          </button>
        </div>
      ) : filteredFAQs.length === 0 ? (
        <div className="empty-state">
          <HelpCircle size={64} />
          <h3>لم نجد نتائج</h3>
          <p>جرب البحث بكلمات مختلفة أو تواصل معنا</p>
        </div>
      ) : (
        <div className="faq-list">
          {filteredFAQs.map(faq => {
            const faqId = faq._id || faq.id;
            return (
              <div 
                key={faqId} 
                className={`faq-item ${expandedId === faqId ? 'expanded' : ''}`}
              >
                <button className="faq-question" onClick={() => toggleExpand(faqId)}>
                  <span>{faq.question}</span>
                  {expandedId === faqId ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="contact-section">
        <h3>لم تجد إجابتك؟</h3>
        <p>تواصل مع فريق الدعم الفني</p>
        <div className="contact-options">
          <a href="mailto:support@msk.sa" className="contact-card">
            <Mail size={24} />
            <span>البريد الإلكتروني</span>
            <span className="contact-value">support@msk.sa</span>
          </a>
          <a href="tel:+966501234567" className="contact-card">
            <Phone size={24} />
            <span>الهاتف</span>
            <span className="contact-value">+966 50 123 4567</span>
          </a>
          <button type="button" className="contact-card" onClick={() => window.open('/chat', '_self')}>
            <MessageCircle size={24} />
            <span>الدردشة المباشرة</span>
            <span className="contact-value">متاح 24/7</span>
          </button>
        </div>
      </div>
    </div>
    </PermissionGate>
  );
};

export default FAQPage;
